import { useState, useEffect, useRef, type FC } from "react";
import { Menu, Settings, LogOut, X, Circle, CheckCircle, Clock, MinusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react";
import { useStore } from "../stores/store";
import { useTranslation } from "react-i18next";
import { SidebarState } from "../stores/global.store";
import { useAuth } from "src/contexts/AuthContext";
import Notifications from "./navigation/notifications";
import type { NavbarDropdownId } from "src/utils/plan-tier";
import CommandPaletteTrigger from "./navbar/CommandPaletteTrigger";
import OrgSwitcher from "./navbar/OrgSwitcher";
import HelpDropdown from "./navbar/HelpDropdown";
import LanguageSelector from "./navbar/LanguageSelector";
import UserMenu from "./navbar/UserMenu";
import PlanBadge from "./navbar/PlanBadge";
// Import the service
import {
  fetchMyAgentStatus,
  updateMyAgentStatus,
  type AgentStatus,
  type AgentStatusResponse
} from "../services/agent-status.service";

interface AppNavbarProps {
  onOpenCommandPalette?: () => void;
}

// Status config for UI with translation keys
const STATUS_CONFIG: Record<AgentStatus, { icon: any; labelKey: string; color: string }> = {
  AVAILABLE: { icon: CheckCircle, labelKey: 'available', color: 'text-green-600' },
  BUSY: { icon: MinusCircle, labelKey: 'busy', color: 'text-red-600' },
  AWAY: { icon: Clock, labelKey: 'away', color: 'text-yellow-600' },
  OFFLINE: { icon: Circle, labelKey: 'offline', color: 'text-gray-400' },
};

// Roles that can use agent status
const AGENT_ROLES = ['owner', 'admin', 'agent'];

const MenuToggle: FC = observer(() => {
  const { globalStore } = useStore();
  const { t } = useTranslation();
  const { toggleSidebar, sidebarState } = globalStore;
  const isCollapsed = sidebarState === SidebarState.COLLAPSED;

  return (
    <button
      onClick={() => toggleSidebar()}
      className="flex size-12 items-center justify-center rounded-full p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      title={isCollapsed ? t("open-sidebar") : t("close-sidebar")}
      aria-label={isCollapsed ? t("open-sidebar") : t("close-sidebar")}
    >
      <Menu size={24} className="stroke-2" />
    </button>
  );
});

// Agent Status Dropdown Component
const AgentStatusDropdown: FC<{
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  userRole: string;
  currentStatus: AgentStatus;
  onStatusChange: (newStatus: AgentStatus) => void;
}> = ({ isOpen, onToggle, onClose, userRole, currentStatus, onStatusChange }) => {
  const { t, i18n } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user has agent role
  const canUseAgentStatus = AGENT_ROLES.includes(userRole?.toLowerCase());

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const updateStatus = async (newStatus: AgentStatus) => {
    // Don't allow updating to same status
    if (newStatus === currentStatus) {
      onClose();
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      console.log('Updating status to:', newStatus);

      const data = await updateMyAgentStatus(
        newStatus,
        `User changed status from ${currentStatus} to ${newStatus}`
      );

      console.log('Status updated successfully:', data);
      onStatusChange(data.status);
      onClose();
    } catch (error) {
      console.error('Failed to update status:', error);
      setError(t('failed-to-update-status'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Don't render anything if user doesn't have agent role
  if (!canUseAgentStatus) {
    return null;
  }

  const CurrentIcon = STATUS_CONFIG[currentStatus].icon;

  // Determine dropdown position based on language (RTL support)
  const dropdownPosition = i18n.language === 'ar' ? 'left-0' : 'right-0';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${isOpen ? 'bg-gray-100' : 'hover:bg-gray-50'
          }`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        disabled={isUpdating}
      >
        <CurrentIcon className={`h-4 w-4 ${STATUS_CONFIG[currentStatus].color}`} />
        <span className="hidden lg:inline">{t(STATUS_CONFIG[currentStatus].labelKey)}</span>
        <span className="sr-only">{t('change-agent-status')}</span>
      </button>

      {isOpen && (
        <div className={`absolute ${dropdownPosition} mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}>
          <div className="py-1" role="menu" aria-orientation="vertical">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t('agent-status')}
            </div>

            {error && (
              <div className="px-4 py-2 text-xs text-red-600 border-b border-gray-100">
                {error}
              </div>
            )}

            {(Object.entries(STATUS_CONFIG) as [AgentStatus, typeof STATUS_CONFIG[AgentStatus]][]).map(([status, config]) => {
              const Icon = config.icon;
              const isSelected = currentStatus === status;
              const isDisabled =
                status === 'OFFLINE' ||
                isUpdating ||
                isSelected; // Disable current status option

              return (
                <button
                  key={status}
                  onClick={() => updateStatus(status)}
                  disabled={isDisabled}
                  className={`
                    w-full text-left px-4 py-2 text-sm flex items-center gap-3
                    ${isSelected ? 'bg-gray-50 cursor-default' : 'hover:bg-gray-50'}
                    ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                    focus-visible:outline-none focus-visible:bg-gray-50
                    ${i18n.language === 'ar' ? 'text-right' : 'text-left'}
                  `}
                  role="menuitem"
                  aria-current={isSelected}
                >
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <span className="flex-1">{t(config.labelKey)}</span>
                  {isSelected && (
                    <CheckCircle className="h-4 w-4 text-primary-600" />
                  )}
                </button>
              );
            })}

            {isUpdating && (
              <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                {t('updating')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AppNavbar: FC<AppNavbarProps> = function ({ onOpenCommandPalette }) {
  const { t, i18n } = useTranslation();
  const logo = "/images/botbat-logo.png";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<NavbarDropdownId | 'agent-status'>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<AgentStatus>('AVAILABLE');
  const { user, logout, currentOrgSubscription } = useAuth();

  // Fetch user role and status when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Try to get role and status from API first
        const status = await fetchMyAgentStatus();
        setUserRole(status.role);
        setCurrentStatus(status.status);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        // Fallback to checking localStorage or user object
        try {
          const org = JSON.parse(localStorage.getItem("currentOrganization") || "null");
          if (org?.userRole) {
            setUserRole(org.userRole);
          }
        } catch (e) {
          console.error('Failed to parse user role from localStorage:', e);
        }
      }
    };

    fetchUserData();
  }, []);

  const handleStatusChange = (newStatus: AgentStatus) => {
    setCurrentStatus(newStatus);
  };

  const handleToggle = (id: NonNullable<typeof activeDropdown>) => {
    setActiveDropdown(prev => (prev === id ? null : id));
  };

  const handleClose = () => setActiveDropdown(null);

  const handleOpenCommandPalette = () => {
    handleClose();
    onOpenCommandPalette?.();
  };

  const handleSignOut = async () => {
    setIsMobileMenuOpen(false);
    await logout();
  };

  // Escape key closes any open dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && activeDropdown !== null) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeDropdown]);

  // Check if user can see agent status in mobile menu
  const canUseAgentStatus = AGENT_ROLES.includes(userRole?.toLowerCase());

  // Language options for mobile menu
  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'ar', label: 'العربية', flag: '🇸🇦' }
  ];

  return (
    <nav
      data-tour="navbar"
      className="bg-white border-b border-gray-200 sticky z-50"
      style={{ top: "0" }}
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* ===== LEFT ZONE: Identity & Context ===== */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <MenuToggle />
            <Link
              to="/"
              className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-md"
            >
              <img src={logo} alt={t("botbat")} className="h-8 w-8" />
              <span className="text-xl font-bold lg:text-xl xl:text-2xl transition-colors duration-300 leading-none bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent whitespace-nowrap">
                {t("botbat")}
              </span>
            </Link>
            <OrgSwitcher
              isOpen={activeDropdown === "org-switcher"}
              onToggle={() => handleToggle("org-switcher")}
              onClose={handleClose}
            />
          </div>

          {/* ===== CENTER ZONE: Search & Navigation ===== */}
          <div className="flex-1 flex justify-center px-4 lg:px-8">
            <CommandPaletteTrigger onOpen={handleOpenCommandPalette} />
          </div>

          {/* ===== RIGHT ZONE: Utilities & Account (Desktop) ===== */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {/* Agent Status Dropdown - only shown for owners/admins/agents */}
            <AgentStatusDropdown
              isOpen={activeDropdown === "agent-status"}
              onToggle={() => handleToggle("agent-status")}
              onClose={handleClose}
              userRole={userRole}
              currentStatus={currentStatus}
              onStatusChange={handleStatusChange}
            />

            <LanguageSelector
              isOpen={activeDropdown === "language"}
              onToggle={() => handleToggle("language")}
              onClose={handleClose}
            />
            <Notifications />
            <HelpDropdown
              isOpen={activeDropdown === "help"}
              onToggle={() => handleToggle("help")}
              onClose={handleClose}
            />
            <UserMenu
              isOpen={activeDropdown === "user"}
              onToggle={() => handleToggle("user")}
              onClose={handleClose}
            />
          </div>

          {/* ===== Mobile menu button ===== */}
          <div className="md:hidden flex items-center gap-2 flex-shrink-0">
            <Notifications />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Language Selector in mobile menu */}
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-2">
                {t('language') || 'Language'}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {languages.map((lang) => {
                  const isActive = i18n.language === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={async () => {
                        // Change language and reload the page to apply RTL properly
                        await i18n.changeLanguage(lang.code);
                        localStorage.setItem('i18nextLng', lang.code);
                        localStorage.setItem('lang', lang.code);
                        // Reload the page to apply the language direction
                        window.location.reload();
                      }}
                      className={`flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 transition-colors ${isActive ? 'bg-gray-50 ring-1 ring-primary-500' : ''
                        }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm font-medium">{lang.label}</span>
                      {isActive && (
                        <CheckCircle className="h-4 w-4 text-primary-600 ml-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Agent Status in mobile menu - only shown for owners/admins/agents */}
            {canUseAgentStatus && (
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  {t('agent-status')}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(STATUS_CONFIG) as [AgentStatus, typeof STATUS_CONFIG[AgentStatus]][]).map(([status, config]) => {
                    const Icon = config.icon;
                    const isSelected = currentStatus === status;

                    return (
                      <button
                        key={status}
                        onClick={async () => {
                          if (status === currentStatus) {
                            setIsMobileMenuOpen(false);
                            return;
                          }
                          try {
                            const data = await updateMyAgentStatus(status);
                            setCurrentStatus(data.status);
                            setIsMobileMenuOpen(false);
                          } catch (error) {
                            console.error('Failed to update status:', error);
                          }
                        }}
                        disabled={status === 'OFFLINE' || status === currentStatus}
                        className={`flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 disabled:opacity-50 ${isSelected ? 'bg-gray-50' : ''
                          }`}
                      >
                        <Icon className={`h-4 w-4 ${config.color}`} />
                        <span className="text-sm">{t(config.labelKey)}</span>
                        {isSelected && (
                          <CheckCircle className="h-4 w-4 text-primary-600 ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Org display for mobile */}
            <div className="px-3 py-2">
              {(() => {
                try {
                  const org = JSON.parse(
                    localStorage.getItem("currentOrganization") || "null",
                  );
                  if (!org) return null;
                  return (
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-50 to-primary-50 flex items-center justify-center border border-blue-100">
                        <span className="text-xs font-bold bg-gradient-to-r from-blue-600 to-primary-600 bg-clip-text text-transparent">
                          {org.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {org.name}
                      </span>
                      <PlanBadge
                        planName={
                          currentOrgSubscription?.subscription?.planName ||
                          org.attributes?.plan?.[0]
                        }
                        size="sm"
                        labelMode="original"
                      />
                    </div>
                  );
                } catch {
                  return null;
                }
              })()}
            </div>
            <div className="border-t border-gray-100 pt-2 mt-2">
              <Link
                to="/account/settings"
                className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Settings className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
                {t("settings")}
              </Link>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                <LogOut className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
                {t("signOut")}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default AppNavbar;